/* ==========================================================================
   F3 Fleet — the keyboard layer

   One registry, one listener, one place that knows what every key in the
   console does.

   THE BAR §6 asks for three things and this file is all three:
     · shortcuts printed in the affordance, in mono          → `caps` renders <kbd>
     · every list arrow-navigable, every dialog Esc-closable → `j`/`k` + the
       Escape discipline in components/motion/focus.ts
     · a focus ring that is never removed                    → styles.css :focus-visible

   WHY A REGISTRY AND NOT JUST HANDLERS
   Eleven surfaces in this build bind their own keys — the transport takes
   `Space , .`, teleop takes `E` and five shifted letters, the dispatch panel
   takes the number row, the 3D viewport takes `1`–`5` while it holds focus.
   Nothing enumerated them, so nothing could tell the operator they existed and
   nothing could stop two of them colliding. SHORTCUTS below is that
   enumeration: this module OWNS the entries marked `own: true` and DOCUMENTS
   the rest, and the sheet (`?`) renders the lot with the ones that do not apply
   to the surface you are standing on dimmed rather than hidden — an operator
   needs to know a key exists somewhere before they can go looking for it.

   WHAT THIS MODULE CLAIMS
     ?  F1     the key sheet
     1 … 6     surface switching, in the same order the strip and the palette
               list them (yielded on /commands, where the number row stages a
               dispatch verb — see BINDINGS)
     j  k      the row cursor: step the primary list of the current surface
               WITHOUT having to Tab into it first
   Everything else on the sheet belongs to the surface that binds it.

   WHAT IT REFUSES TO CLAIM
     A bare key is dead while the operator is typing, while a `role=application`
     widget (the 3D viewports) holds focus, while a modal is open, and whenever
     any modifier is down. Those four guards are the whole reason a console can
     afford bare keys at all.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Surfaces
   -------------------------------------------------------------------------- */

import { announce } from '../components/motion/announce.ts';
import { steerFocus } from '../components/motion/focus.ts';

export type SurfaceKey =
  | 'fleet'
  | 'list'
  | 'map'
  | 'detail'
  | 'teleop'
  | 'scan'
  | 'telemetry'
  | 'commands'
  | 'catalog';

export interface Surface {
  key: SurfaceKey;
  /** The digit that switches to it, when it has one. */
  digit: string | null;
  label: string;
  path: string;
}

/* The six switchable surfaces, in the order the surface strip and the command
   palette already list them. One enumeration or three would drift. */
export const SURFACES: readonly Surface[] = [
  { key: 'fleet', digit: '1', label: 'Fleet operations', path: '/fleet' },
  { key: 'list', digit: '2', label: 'Robot list', path: '/fleet/list' },
  { key: 'map', digit: '3', label: 'Fleet map', path: '/fleet/map' },
  { key: 'telemetry', digit: '4', label: 'Telemetry', path: '/telemetry' },
  { key: 'commands', digit: '5', label: 'Commands', path: '/commands' },
  { key: 'catalog', digit: '6', label: 'Catalog', path: '/catalog' },
];

/** Which surface a pathname is. Mirrors router/parsePath + useFleetRoute. */
export function surfaceOf(pathname: string): SurfaceKey {
  const parts = pathname.replace(/^\/+/, '').replace(/\/+$/, '').split('/').filter(Boolean);
  const head = parts[0] ?? '';
  if (head === 'telemetry') return 'telemetry';
  if (head === 'commands') return 'commands';
  if (head === 'catalog') return 'catalog';
  const view = head === 'fleet' ? (parts[1] ?? '') : head;
  switch (view) {
    case 'list':
      return 'list';
    case 'map':
      return 'map';
    case 'detail':
      return 'detail';
    case 'teleop':
      return 'teleop';
    case 'scan':
      return 'scan';
    default:
      return 'fleet';
  }
}

/* --------------------------------------------------------------------------
   The registry

   Every key the console answers to, in one table, with — for each one — the
   PROOF that it is live. A row without a proof is a claim, and a claim on a
   help sheet is the kind of defect an operator only discovers at the moment
   they needed the key.

   `proof` is a selector for the thing on screen that the binding acts on or is
   printed on. The sheet resolves it against the live document, dims a row that
   does not resolve, and `auditKeymap()` turns a row that SHOULD resolve here
   and does not into a loud dev-time failure. So the sheet cannot drift from
   the build: if another surface renames the control a key drives, the row goes
   dim and the console says so, rather than printing a key that does nothing.

   `proof: null` means THIS module binds the key unconditionally — the only
   rows allowed to assert themselves.
   -------------------------------------------------------------------------- */

/** `global` = honoured on every surface; otherwise the surface that binds it.
    `tables` and `controls` are honoured wherever the widget they name is on
    screen, which the sheet MEASURES rather than declares. */
export type Scope = 'global' | 'tables' | 'controls' | SurfaceKey;

export interface Shortcut {
  /** Key caps in order. Adjacent caps are a chord unless `sep` says otherwise. */
  caps: readonly string[];
  /** 'or' = alternatives, 'to' = a contiguous range. */
  sep?: 'or' | 'to';
  label: string;
  /** A qualification the operator has to know. One short clause, no period. */
  note?: string;
  scope: Scope;
  /** True when THIS module implements it; false when a surface does. */
  own: boolean;
  /** A selector for the control this key drives, or null for a key this module
      binds unconditionally. Resolved against the live document. */
  proof: string | null;
  /** Narrows `proof` to elements whose text or label matches. */
  proofText?: string;
  /** When exactly one element matches, the layer publishes the key ON it as
      `aria-keyshortcuts`, so a screen reader hears the shortcut from the
      control itself and not only from this sheet (THE BAR §6). */
  stamp?: string;
  /** Surfaces that take this key for something of their own, so the global
      binding stands down there. The dispatch panel spends the number row on
      its verbs, and a verb is worth more than a shortcut to a screen two keys
      away — so `1`–`6` is dim on Commands, with the note saying why. */
  except?: readonly SurfaceKey[];
  /** The key genuinely does not exist on every surface its scope covers — `/`
      needs a filter box, `x` needs a selectable row, ⌘S needs the registry
      form. Such a row dims rather than lying, and its absence is information
      rather than drift. Every OTHER row must resolve on the surface it claims,
      and `auditKeymap()` fails loudly when one does not. */
  conditional?: boolean;
}

export interface ShortcutGroup {
  key: Scope;
  title: string;
  items: readonly Shortcut[];
}

/** macOS prints ⌘; every other platform prints Ctrl. A ⌘ on a Linux
    workstation is not a shortcut, it is a glyph nobody can press. */
export const APPLE: boolean =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

const MOD = APPLE ? '⌘' : 'Ctrl';
const MOD_STAMP = APPLE ? 'Meta' : 'Control';

/* ---- the seams other surfaces publish, named once ---------------------- */

/** The surface's own filter box. `/` focuses it, from anywhere — implemented
    in this module (see installKeyboardLayer) rather than left to the one
    surface that had bound it, which is why the Everywhere block may print it. */
export const FILTER_SELECTOR = [
  'main input[type="search"]',
  'main input[aria-label*="filter" i]',
  'main input[aria-label*="search" i]',
  'main input[placeholder*="filter" i]',
  'main input[placeholder*="search" i]',
].join(',');

/** Anything this build calls a segmented control, a tab strip or a surface
    strip — the three composites components/motion/widgets.ts drives. */
const COMPOSITE = '[role="radiogroup"],[role="tablist"],[data-tab]';

/** A popover: a trigger that says it opens a dialog and currently has. */
const POPOVER_TRIGGER = '[aria-haspopup="dialog"][aria-expanded]';

export function shortcutGroups(): readonly ShortcutGroup[] {
  return [
    {
      key: 'global',
      title: 'Everywhere',
      items: [
        {
          caps: [MOD, 'K'],
          label: 'Search robots and views',
          scope: 'global',
          own: false,
          proof: `[aria-keyshortcuts~="${MOD_STAMP}+K"]`,
        },
        {
          caps: ['1', '6'],
          sep: 'to',
          label: 'Switch surface',
          note: 'except on Commands',
          scope: 'global',
          own: true,
          proof: null,
          except: ['commands'],
        },
        { caps: ['?'], label: 'This list', scope: 'global', own: true, proof: null },
        {
          /* Bound by the app shell in a global effect, so it is live on every
             surface — including the two (robot detail, the point-cloud
             viewer) that render no hold switch at all, which is a missing
             AFFORDANCE rather than a missing key. Proved by nothing for that
             reason; see the note in the lane report. */
          caps: ['L'],
          label: 'Hold or resume every poll',
          scope: 'global',
          own: false,
          proof: null,
        },
        {
          caps: ['/'],
          label: "Focus the surface's filter",
          scope: 'global',
          own: true,
          proof: FILTER_SELECTOR,
          stamp: 'Slash',
          conditional: true,
        },
        {
          caps: ['Esc'],
          label: 'Close the dialog or popover',
          scope: 'global',
          own: true,
          proof: null,
        },
      ],
    },
    {
      key: 'tables',
      title: 'Lists and tables',
      items: [
        {
          caps: ['j'],
          label: 'Next row',
          note: 'from anywhere',
          scope: 'tables',
          own: true,
          proof: ROW_SELECTOR,
        },
        { caps: ['k'], label: 'Previous row', scope: 'tables', own: true, proof: ROW_SELECTOR },
        {
          caps: ['↑', '↓'],
          sep: 'or',
          label: 'Step row',
          scope: 'tables',
          own: false,
          proof: ROW_SELECTOR,
        },
        {
          caps: ['Home', 'End'],
          sep: 'or',
          label: 'First or last row',
          scope: 'tables',
          own: true,
          proof: ROW_SELECTOR,
        },
        {
          caps: ['⏎', 'Space'],
          sep: 'or',
          label: 'Open the row',
          scope: 'tables',
          own: false,
          proof: ROW_SELECTOR,
        },
        {
          caps: ['x'],
          label: 'Select or deselect the row',
          scope: 'tables',
          own: true,
          /* Published by widgets.ts on the row's own select box, so this row
             goes dim on a list whose rows cannot be selected. */
          proof: '[aria-keyshortcuts="X"]',
          conditional: true,
        },
      ],
    },
    {
      key: 'controls',
      title: 'Controls',
      items: [
        {
          caps: ['←', '→'],
          sep: 'or',
          label: 'Move along a segmented control or tab strip',
          scope: 'controls',
          own: true,
          proof: COMPOSITE,
        },
        {
          caps: ['Home', 'End'],
          sep: 'or',
          label: 'Jump to either end',
          scope: 'controls',
          own: true,
          proof: COMPOSITE,
        },
        {
          caps: ['⏎', 'Space'],
          sep: 'or',
          label: 'Choose what the cursor is on',
          note: 'where moving does not',
          scope: 'controls',
          own: true,
          proof: COMPOSITE,
        },
        {
          /* components/motion/scroll.ts makes a panel that clips its content a
             focusable region, which is what Chrome needs before it will hand
             it the scroll keys at all. Conditional because whether a panel
             clips depends on the viewport, which is the honest answer: the
             row lights up on the surface and at the height where it does. */
          caps: ['↑', '↓', 'PgUp', 'PgDn'],
          sep: 'or',
          label: 'Scroll a clipped panel',
          note: 'once Tab is inside',
          scope: 'controls',
          own: true,
          proof: '[data-f3-scroll="region"]',
          conditional: true,
        },
        {
          caps: ['Tab'],
          label: 'Stays inside an open popover',
          scope: 'controls',
          own: true,
          proof: POPOVER_TRIGGER,
          conditional: true,
        },
        {
          caps: ['Esc'],
          label: 'Close the popover, back to its button',
          scope: 'controls',
          own: true,
          proof: POPOVER_TRIGGER,
          conditional: true,
        },
      ],
    },
    {
      key: 'map',
      title: 'Fleet map',
      items: [
        {
          caps: ['Space'],
          label: 'Play or pause the replay',
          scope: 'map',
          own: false,
          proof: 'button[aria-label="Play replay"],button[aria-label="Pause replay"]',
          stamp: 'Space',
        },
        {
          caps: [','],
          label: 'Step back one bucket',
          scope: 'map',
          own: false,
          proof: 'button[aria-label="Step back one bucket"]',
          stamp: ',',
        },
        {
          caps: ['.'],
          label: 'Step forward one bucket',
          scope: 'map',
          own: false,
          proof: 'button[aria-label="Step forward one bucket"]',
          stamp: '.',
        },
        {
          caps: ['←', '→'],
          sep: 'or',
          label: 'Drag the window edge',
          note: 'on the track band',
          scope: 'map',
          own: false,
          proof: '[role="slider"][aria-label*="Track window leading edge"]',
          conditional: true,
        },
        {
          caps: ['End'],
          label: 'Return the band to live',
          note: 'on the track band',
          scope: 'map',
          own: false,
          proof: '[role="slider"][aria-label*="Track window leading edge"]',
          conditional: true,
        },
        {
          caps: ['Esc'],
          label: 'Clear the selected robot',
          scope: 'map',
          own: false,
          proof: '[data-row]',
        },
      ],
    },
    {
      key: 'detail',
      title: 'Robot detail',
      items: [
        {
          caps: ['←', '→'],
          sep: 'or',
          label: 'Move the reading cursor',
          note: 'on a vitals trace',
          scope: 'detail',
          own: false,
          proof: '[role="group"][aria-label*="over the telemetry window"]',
        },
        {
          caps: ['Esc'],
          label: 'Drop the reading cursor',
          scope: 'detail',
          own: false,
          proof: '[role="group"][aria-label*="over the telemetry window"]',
        },
        {
          caps: ['←', '→'],
          sep: 'or',
          label: 'Pan the telemetry window',
          note: 'on the window slider',
          scope: 'detail',
          own: false,
          proof: '[role="slider"][aria-label*="Telemetry window position"]',
          conditional: true,
        },
        {
          caps: ['End'],
          label: 'Return the window to live',
          note: 'on the window slider',
          scope: 'detail',
          own: false,
          proof: '[role="slider"][aria-label*="Telemetry window position"]',
          conditional: true,
        },
        {
          caps: [MOD, 'S'],
          label: 'Save the registry record',
          note: 'Configuration tab',
          scope: 'detail',
          own: false,
          proof: 'button[type="submit"]',
          proofText: 'save',
          conditional: true,
        },
      ],
    },
    {
      key: 'commands',
      title: 'Commands',
      items: [
        {
          caps: ['1', '0'],
          sep: 'to',
          label: 'Stage the nth verb',
          note: 'on the dispatch bench',
          scope: 'commands',
          own: false,
          /* The verb grid when the bench has the rail, and the tab that brings
             it back when the record does — the key works from either, and it
             used to be proved by a robot picker that is inside the hidden pane,
             so the sheet printed a key the audit could not find. */
          proof: '[data-kbd="commands-verbs"], #rail-tab-dispatch',
        },
        {
          caps: [MOD, '⏎'],
          label: 'Issue the staged command',
          note: 'with a verb staged',
          scope: 'commands',
          own: false,
          /* Named on the commit button itself rather than matched on the word
             "send", which also matched nothing while the bench was off screen. */
          proof: '[data-kbd="commands-send"]',
          stamp: `${MOD_STAMP}+Enter`,
          /* There is no staged command to issue until something is staged, and
             the bench is not on screen until then either. The row dims and says
             why, which is what `conditional` is for. */
          conditional: true,
        },
        {
          caps: ['Esc'],
          label: 'Stand down the staged command',
          scope: 'commands',
          own: false,
          proof: 'button',
          proofText: '^cancel',
        },
      ],
    },
    {
      key: 'teleop',
      title: 'Teleoperation',
      items: [
        {
          caps: ['E'],
          label: 'Emergency stop',
          note: 'always armed',
          scope: 'teleop',
          own: false,
          proof: 'button',
          proofText: '^e-?stop',
          stamp: 'E',
        },
        {
          caps: ['Shift', 'X'],
          label: 'Safe stop',
          scope: 'teleop',
          own: false,
          proof: 'button',
          proofText: '^safe stop',
          stamp: 'Shift+X',
        },
        {
          caps: ['Shift', 'H'],
          label: 'Return home',
          scope: 'teleop',
          own: false,
          proof: 'button',
          proofText: '^return home',
          stamp: 'Shift+H',
        },
        {
          caps: ['Shift', 'L'],
          label: 'Go live',
          scope: 'teleop',
          own: false,
          proof: 'button',
          proofText: '^go live',
          stamp: 'Shift+L',
        },
        {
          caps: ['Shift', 'D'],
          label: 'Open this robot’s detail',
          scope: 'teleop',
          own: false,
          proof: 'button',
          proofText: '^robot detail',
          stamp: 'Shift+D',
        },
        {
          caps: ['Shift', 'K'],
          label: 'Link health',
          scope: 'teleop',
          own: false,
          proof: 'button[aria-label="Take control of this robot"]',
        },
      ],
    },
    {
      key: 'scan',
      title: 'Point-cloud viewer',
      /* Every row here is `conditional`, and it is not a dodge. `/fleet/scan/:id`
         is the record of a CAPTURE SESSION; the 3D viewport only exists once
         the COPC worker has derived a cloud from it. On a `raw`, `processing`
         or `failed` session there is no `[role="application"]` on the page and
         these six keys genuinely do nothing — so the sheet dims them and says
         so, instead of printing six shortcuts that resolve to nothing. */
      items: [
        {
          caps: ['←→↑↓'],
          label: 'Orbit the focused pane',
          scope: 'scan',
          own: false,
          proof: '[role="application"]',
          conditional: true,
        },
        {
          caps: ['+', '−'],
          sep: 'or',
          label: 'Zoom',
          scope: 'scan',
          own: false,
          proof: '[role="application"]',
          conditional: true,
        },
        {
          caps: ['R'],
          label: 'Reset the focused pane',
          scope: 'scan',
          own: false,
          proof: '[role="application"]',
          conditional: true,
        },
        {
          caps: ['F'],
          label: 'Reset every pane',
          scope: 'scan',
          own: false,
          proof: '[role="application"]',
          conditional: true,
        },
        {
          caps: ['S'],
          label: 'Fold the settings rail',
          scope: 'scan',
          own: false,
          proof: '[role="application"]',
          conditional: true,
        },
        {
          caps: ['1', '5'],
          sep: 'to',
          label: 'Load a view preset',
          note: 'viewport focused',
          scope: 'scan',
          own: false,
          proof: '[role="tablist"][aria-label="View presets"]',
          conditional: true,
        },
        {
          caps: ['?'],
          label: 'The viewport’s own key list',
          note: 'not this one',
          scope: 'scan',
          own: false,
          proof: '[role="application"]',
          conditional: true,
        },
      ],
    },
  ];
}

/* --------------------------------------------------------------------------
   Proof — is this key live, here, now?
   -------------------------------------------------------------------------- */

function onScreen(el: Element): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (!el.isConnected) return false;
  /* The console layer's own controls never prove a surface's key. */
  if (el.closest('[data-f3-layer]') !== null) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
  /* Deliberately NOT an `[inert]` check. The sheet that asks these questions
     is modal, so focus.ts has just made the whole surface behind it inert —
     testing for that would answer "no key works" every single time the
     operator opens the list. The question is whether the key works on this
     surface, not whether it works while the operator is reading about it. */
}

/** Elements that could prove a shortcut, cheapest test first.

    Order matters for cost, not correctness: `proofText` rows select on `button`
    — 200 of them on the dashboard — and a `getBoundingClientRect` per candidate
    is a forced layout per candidate. The name test is free by comparison, so it
    runs first and geometry is measured on the one or two that survive it. */
function candidates(s: Shortcut): HTMLElement[] {
  if (s.proof === null || typeof document === 'undefined') return [];
  let found = Array.from(document.querySelectorAll<HTMLElement>(s.proof));
  if (s.proofText !== undefined) {
    const re = new RegExp(s.proofText, 'i');
    found = found.filter((el) => re.test((el.getAttribute('aria-label') ?? el.textContent ?? '').trim()));
  }
  return found.filter(onScreen);
}

/** The element that proves a shortcut, or null. `proofText` matches against the
    accessible name where there is one and the rendered text otherwise, which is
    how a key printed on its own button (`Safe stop ⇧X`) is found without
    depending on a CSS-module class hash. */
export function proofFor(s: Shortcut): HTMLElement | null {
  return candidates(s)[0] ?? null;
}

/** Whether the key's control is on screen. A `proof: null` row is bound
    unconditionally and is always true. */
export function isLive(s: Shortcut): boolean {
  return s.proof === null || proofFor(s) !== null;
}

/** Whether the operator can press this key on THIS surface and have something
    happen — the one rule the sheet draws with and the audit asserts against.

    Scope first, proof second. A surface's keys are bound by that surface, so a
    control that happens to look like teleop's E-stop on some other screen must
    not light its row (a `failed · ESTOP e-stop engaged` reason cell in the
    command queue did exactly that); `global`, `tables` and `controls` are
    honoured wherever their widget is, which is what the proof measures. */
export function isLiveOn(s: Shortcut, surface: SurfaceKey): boolean {
  return inScope(s, surface) && isLive(s);
}

function inScope(s: Shortcut, surface: SurfaceKey): boolean {
  if (s.except !== undefined && s.except.includes(surface)) return false;
  return (
    s.scope === 'global' || s.scope === 'tables' || s.scope === 'controls' || s.scope === surface
  );
}

/** Publish each resolved key on the control it drives, as `aria-keyshortcuts`.

    Only where exactly one control matches — an ambiguous match would announce
    a shortcut on the wrong button, which is worse than announcing none — and
    only for keys in scope on the surface in front of us, which is also what
    keeps this off the 200-button scan on a surface whose keys it cannot be. */
export function stampAffordances(surface?: SurfaceKey): void {
  if (typeof document === 'undefined') return;
  const here = surface ?? surfaceOf(window.location.pathname);
  for (const group of shortcutGroups()) {
    for (const item of group.items) {
      if (item.stamp === undefined || item.proof === null) continue;
      if (!inScope(item, here)) continue;
      const hits = candidates(item);
      const only = hits.length === 1 ? hits[0] : undefined;
      if (!only) continue;
      if (only.getAttribute('aria-keyshortcuts') !== item.stamp) {
        only.setAttribute('aria-keyshortcuts', item.stamp);
      }
    }
  }
}

/* --------------------------------------------------------------------------
   The audit

   The assertion that keeps this sheet honest. For the surface in front of us,
   every row the sheet would print as APPLICABLE has to resolve to something on
   screen. A row that does not is either a key that was removed, a control that
   was renamed, or a claim that was never true — and all three are the same
   defect to the operator who presses the key.

   Run from install.tsx in dev on every route change, and by the lane's own
   Playwright harness on every surface.
   -------------------------------------------------------------------------- */

export interface KeymapAudit {
  surface: SurfaceKey;
  path: string;
  /** Rows the sheet prints as live here. */
  live: string[];
  /** Rows scoped to THIS surface that resolve to nothing — the drift. */
  drift: string[];
}

function rowName(g: ShortcutGroup, s: Shortcut): string {
  return `${g.title} · ${s.caps.join(s.sep === 'to' ? '–' : '/')} — ${s.label}`;
}

export function auditKeymap(pathname = window.location.pathname): KeymapAudit {
  const surface = surfaceOf(pathname);
  const live: string[] = [];
  const drift: string[] = [];
  for (const g of shortcutGroups()) {
    for (const s of g.items) {
      const ok = isLiveOn(s, surface);
      if (ok) live.push(rowName(g, s));
      /* Only a row that CLAIMS this surface counts as drift. A teleop key is
         supposed to resolve to nothing while we stand on the fleet map. */
      else if (s.conditional === true) continue;
      else if (s.except !== undefined && s.except.includes(surface)) continue;
      else if (s.scope === surface || s.scope === 'global') drift.push(rowName(g, s));
    }
  }
  return { surface, path: pathname, live, drift };
}

/* --------------------------------------------------------------------------
   Guards
   -------------------------------------------------------------------------- */

/** One definition of "the operator is typing". Two would eventually disagree
    and eat a letter out of a filter box. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/** A `role=application` widget (the 3D viewports) owns every key while it holds
    focus. Screen readers make the same promise; so do we. */
function inApplication(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest('[role="application"]') !== null;
}

function modalOpen(): boolean {
  return document.querySelector('[role="dialog"][aria-modal="true"]') !== null;
}

/** A dialog owns the keyboard while the operator is standing in it — a modal
    one, and equally the non-modal popovers (Columns, Filters, transport
    detail). `3` inside the column popover used to walk the operator off to the
    fleet map with the panel still open behind them. */
function inDialog(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest('[role="dialog"]') !== null;
}

/* --------------------------------------------------------------------------
   The row cursor

   `j`/`k` step the primary list of whatever is on screen. The point is not to
   duplicate the arrow keys — DataTable already binds those on a focused row —
   it is that reaching the first row costs an operator a dozen presses of Tab.
   `j` puts them in it.
   -------------------------------------------------------------------------- */

/** What counts as a row. Three contracts, because three surfaces emit three:
    DataTable stamps `data-rowkey` on every `<tr>`, the card lists stamp
    `data-kbd-row`, and the fleet map's roster stamps `data-row="robot"` on the
    buttons it already arrow-navigates. Reading all three is what lets `j` enter
    the map's roster from anywhere instead of only after a dozen Tabs. */
export const ROW_SELECTOR = 'tr[data-rowkey], [data-kbd-row], [data-row]';

function visible(el: HTMLElement): boolean {
  if (!el.isConnected) return false;
  if (el.closest('[inert]') !== null) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

/** Every candidate row on screen, in document order. */
export function allRows(): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>(ROW_SELECTOR)).filter(visible);
}

/** The list a row belongs to.

    Not `row.parentElement`: the fleet map's roster splits its 27 rows across
    collapsible groups, so the parent is a group and `j` would stop dead at the
    end of `Fremont (9)` with eighteen robots still below it. So: the nearest
    ancestor holding more than one row, then climbed for as long as the parent
    holds MORE rows still. A grouped roster resolves to the whole roster; two
    tables on one surface stay two, because a table holds exactly the rows its
    tbody does and the climb stops there. */
function listOf(
  row: HTMLElement,
  rows: readonly HTMLElement[],
  cache: Map<HTMLElement, HTMLElement | null>,
): HTMLElement | null {
  const from = row.parentElement;
  if (!from) return null;
  const hit = cache.get(from);
  if (hit !== undefined) return hit;

  const count = (el: HTMLElement): number => rows.reduce((n, r) => (el.contains(r) ? n + 1 : n), 0);
  let node: HTMLElement | null = from;
  while (node && node !== document.body && count(node) < 2) node = node.parentElement;
  if (!node || node === document.body) {
    cache.set(from, from);
    return from;
  }
  let best = node;
  let held = count(best);
  for (let parent = best.parentElement; parent && parent !== document.body; ) {
    const n = count(parent);
    if (n <= held) break;
    best = parent;
    held = n;
    parent = parent.parentElement;
  }
  cache.set(from, best);
  return best;
}

/** Every row on screen, bucketed into the list it belongs to, in document
    order. One definition, shared by the `j`/`k` cursor and by the roving
    tabindex in components/motion/widgets.ts — two answers to "which list is
    this row in" would eventually disagree, and the disagreement would show up
    as a cursor that steps somewhere the Tab key cannot follow. */
export function rowLists(rows: readonly HTMLElement[]): Map<HTMLElement, HTMLElement[]> {
  const cache = new Map<HTMLElement, HTMLElement | null>();
  const out = new Map<HTMLElement, HTMLElement[]>();
  for (const row of rows) {
    const list = listOf(row, rows, cache);
    if (!list) continue;
    const bucket = out.get(list);
    if (bucket) bucket.push(row);
    else out.set(list, [row]);
  }
  return out;
}

/** The list the cursor belongs to: the one holding focus, else the one showing
    the most of itself in the viewport. "Biggest on screen" is the honest
    reading of "the list the operator is looking at". */
function primaryList(lists: Map<HTMLElement, HTMLElement[]>): HTMLElement | null {
  const active = document.activeElement;
  if (active instanceof HTMLElement) {
    const own = active.closest<HTMLElement>(ROW_SELECTOR);
    if (own) {
      for (const [list, members] of lists) if (members.includes(own)) return list;
    }
  }
  const vh = window.innerHeight || 0;
  let best: HTMLElement | null = null;
  let bestArea = 0;
  for (const [list, members] of lists) {
    let area = 0;
    for (const row of members) {
      const r = row.getBoundingClientRect();
      area += Math.max(0, Math.min(r.bottom, vh) - Math.max(r.top, 0));
    }
    if (area > bestArea) {
      best = list;
      bestArea = area;
    }
  }
  return best;
}

/** The row's own selection control, by DataTable's published name for it:
    `aria-label="Select <rowkey>"`. Defined here rather than in widgets.ts so
    that the two modules that need it — the `x` binding below and the roving
    tabindex that takes it out of the tab sequence — do not import each other. */
export function rowSelectBox(row: HTMLElement): HTMLInputElement | null {
  for (const box of Array.from(row.querySelectorAll<HTMLInputElement>('input[type="checkbox"]'))) {
    if (/^select\s/i.test(box.getAttribute('aria-label') ?? '')) return box;
  }
  return null;
}

/** Returns the row it landed on, or null when there was no list to move in. */
export function moveRowCursor(delta: 1 | -1): HTMLElement | null {
  const rows = allRows();
  if (rows.length === 0) return null;
  const lists = rowLists(rows);
  const list = primaryList(lists);
  if (!list) return null;
  const inList = lists.get(list) ?? [];
  if (inList.length === 0) return null;

  const active = document.activeElement;
  const current =
    active instanceof HTMLElement ? active.closest<HTMLElement>(ROW_SELECTOR) : null;
  const at = current ? inList.indexOf(current) : -1;

  /* No cursor yet: `j` enters at the top, `k` at the bottom. Stepping off
     either end holds — a list that wraps silently moves an operator 30 rows
     without telling them. */
  let next: HTMLElement | undefined;
  if (at < 0) next = delta === 1 ? inList[0] : inList[inList.length - 1];
  else next = inList[Math.min(inList.length - 1, Math.max(0, at + delta))];
  if (!next) return null;

  if (next.tabIndex < 0 && !next.hasAttribute('tabindex')) next.tabIndex = -1;
  /* Steered, not plain: `j` from a mouse-driven session moves the cursor
     without the browser calling it keyboard focus, and an unmarked landing is
     a cursor the operator cannot see. See components/motion/focus.ts. */
  steerFocus(next);
  next.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  return next;
}

/* --------------------------------------------------------------------------
   Install
   -------------------------------------------------------------------------- */

export interface KeyboardHost {
  /** Push a path through the SPA router. */
  navigate: (path: string) => void;
  toggleSheet: () => void;
  sheetOpen: () => boolean;
  /** Where the keyboard goes when it steps out of a filter box. */
  leaveField: () => void;
  /** Fired once, on the operator's first bare keystroke, ever. */
  onFirstKey?: () => void;
}

/* useBrowserRoute fans pushState out over this event; a module outside React
   has to raise it or every consumer of the route would drift. Named here
   because the constant in router/useBrowserRoute.ts is private. */
export const PATH_CHANGE_EVENT = 'fleet:path-change';

export function navigateTo(path: string): void {
  if (window.location.pathname === path) return;
  window.history.pushState(null, '', path);
  window.dispatchEvent(new Event(PATH_CHANGE_EVENT));
}

export function installKeyboardLayer(host: KeyboardHost): () => void {
  let firstKeyDone = false;
  try {
    firstKeyDone = window.localStorage.getItem('fleet-kbd-seen') === '1';
  } catch {
    firstKeyDone = true; /* private mode — skip the hint rather than repeat it */
  }

  /* `silent` records that this operator drives from the keyboard without
     showing them the hint — used by `?` itself, where the hint would be
     telling them what they have just done. */
  function markFirstKey(silent = false): void {
    if (firstKeyDone) return;
    firstKeyDone = true;
    try {
      window.localStorage.setItem('fleet-kbd-seen', '1');
    } catch {
      /* nothing to remember it with; the hint has still been shown once */
    }
    if (!silent) host.onFirstKey?.();
  }

  function onKeyDown(e: KeyboardEvent): void {
    if (e.defaultPrevented || e.isComposing) return;

    /* The sheet is the one dialog this module owns, so `?` and Esc reach
       through it; nothing else does. */
    if (host.sheetOpen()) {
      if (e.key === 'Escape') {
        e.preventDefault();
        host.toggleSheet();
        return;
      }
      if (e.key === '?' || e.key === 'F1') {
        e.preventDefault();
        host.toggleSheet();
        return;
      }
      /* ⌘K is the shell's, and it opens a modal of its own. Step out of the
         way rather than stacking two dialogs — but do not swallow the key: the
         operator asked for the palette and they should get it. */
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') host.toggleSheet();
      return;
    }

    if (e.metaKey || e.ctrlKey || e.altKey) return;

    if (isTypingTarget(e.target)) return;
    if (inApplication(e.target)) return;
    if (inDialog(e.target)) return;
    if (modalOpen()) return;

    /* `?` and F1 are the only keys that open something. Everything below is a
       move, so a stray press costs a scroll position at worst. */
    if (e.key === '?' || e.key === 'F1') {
      e.preventDefault();
      host.toggleSheet();
      markFirstKey(true);
      return;
    }

    /* `/` puts the keyboard in the surface's filter box.

       THE BAR §6 lists it as one of the console's standing keys, and it was
       bound on exactly one surface — so the sheet printed it under EVERYWHERE
       and it did nothing on four of the five. Implemented here instead, off
       the same selector the sheet proves the row with, so the key and the
       claim cannot come apart. Surfaces with no filter (Operations, the signal
       explorer) have nothing to focus and the sheet dims the row there. */
    if (e.key === '/' && !e.shiftKey) {
      const box = document.querySelector<HTMLInputElement>(FILTER_SELECTOR);
      if (!box || box.closest('[inert]') !== null) return;
      const r = box.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) return;
      e.preventDefault();
      box.focus({ preventScroll: true });
      box.select();
      markFirstKey();
      return;
    }

    /* `x` selects the row under the cursor. It exists because the row's own
       checkbox is no longer a tab stop (components/motion/widgets.ts): the row
       is the composite and this is the key that reaches into it. Space and
       Enter are already spent — DataTable opens the row with them. */
    if (e.key === 'x' && !e.shiftKey) {
      const active = e.target;
      const row =
        active instanceof HTMLElement ? active.closest<HTMLElement>(ROW_SELECTOR) : null;
      const box = row === null ? null : rowSelectBox(row);
      if (!box) return;
      e.preventDefault();
      box.click();
      markFirstKey();
      const name = (box.getAttribute('aria-label') ?? '').replace(/^select\s+/i, '');
      announce(`${box.checked ? 'Selected' : 'Deselected'} ${name}`);
      return;
    }

    if (e.key === 'j' || e.key === 'k') {
      if (e.shiftKey) return;
      const landed = moveRowCursor(e.key === 'j' ? 1 : -1);
      if (landed) {
        e.preventDefault();
        markFirstKey();
      }
      return;
    }

    if (/^[1-9]$/.test(e.key)) {
      /* The dispatch panel spends the number row on its verbs, and a verb is
         worth more than a shortcut to a screen two keys away. */
      if (window.location.pathname.startsWith('/commands')) return;
      const surface = SURFACES.find((s) => s.digit === e.key);
      if (!surface) return;
      e.preventDefault();
      host.navigate(surface.path);
      markFirstKey();
      return;
    }

    /* Any deliberate navigation key counts as "this operator drives from the
       keyboard", which is the one thing the hint is waiting to learn. */
    if (e.key === 'Tab' || e.key.startsWith('Arrow') || e.key.length === 1) markFirstKey();
  }

  /* Escape out of a filter box, in the two steps an operator expects: the
     first press is the SURFACE's — it clears the query — and the second hands
     the keyboard back. This has to read `.value` in the CAPTURE phase: React
     flushes a discrete event synchronously inside its own listener, so by the
     time a bubble-phase listener runs the field it is about to clear already
     reads empty and both steps would collapse into one. */
  function onEscapeCapture(e: KeyboardEvent): void {
    if (e.key !== 'Escape' || e.metaKey || e.ctrlKey || e.altKey) return;
    if (modalOpen()) return;
    const t = e.target;
    if (!(t instanceof HTMLInputElement) && !(t instanceof HTMLTextAreaElement)) return;
    if (t.value !== '') return;
    t.blur();
    host.leaveField();
  }

  window.addEventListener('keydown', onEscapeCapture, true);
  window.addEventListener('keydown', onKeyDown);
  return () => {
    window.removeEventListener('keydown', onEscapeCapture, true);
    window.removeEventListener('keydown', onKeyDown);
  };
}
